/**
 * The playback states are goverened by a finite state machine operated by the
 * adapters. The states and transitions of the state machine aim to smooth over
 * the browser and technology differences, and provide user interfaces and
 * tracking integrations with clear and predictable behavior.
 *
 * There are three sets of states, which together form a hierarchy. The
 * {@link PlaybackState}s are the main states, and the {@link WaitingReason}
 * and {@link PausedReason} states are substates of `Playing` and `Paused`.
 */

/**
 * The main states.
 */
let PlaybackState;
/**
 * Reasons for time not running while in playing state. When paused, seeking
 * can be a reason for waiting.
 *
 * Tip: It might be nice to display a "The network seems slow. Wait or reload?"
 * message to the user after, for example, 20 seconds of waiting.
 */

(function (PlaybackState) {
  PlaybackState["Idle"] = "idle";
  PlaybackState["Playing"] = "playing";
  PlaybackState["Paused"] = "paused";
  PlaybackState["Broken"] = "broken";
})(PlaybackState || (PlaybackState = {}));

let WaitingReason;
/**
 * The paused reasons only apply to the `Paused` main state, and only the
 * player has paused by itself. Otherwise, the paused reason is `undefined`.
 */

(function (WaitingReason) {
  WaitingReason["Starting"] = "starting";
  WaitingReason["Seeking"] = "seeking";
  WaitingReason["Rebuffering"] = "rebuffering";
})(WaitingReason || (WaitingReason = {}));

let PausedReason;

(function (PausedReason) {
  PausedReason["AutoplayBlocked"] = "autoplayblocked";
  PausedReason["Ended"] = "ended";
})(PausedReason || (PausedReason = {}));

/**
 * There are two ways to listen to events; through an event emitter on the
 * session object (`on`, `once`, `off`) or through
 * [CustomEvents](https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Creating_and_triggering_events)
 * on the container element.
 *
 * Session event emitter example:
 *
 * ```ts
 * import { CoreEvent, PlaybackSession } from '@nrk/player-core';
 *
 * const session: PlaybackSession = ...;
 *
 * session.on(CoreEvent.WaitingChange, (event) => toggleSpinner(event.isWaiting));
 * session.once(CoreEvent.TimeRunningChange, (event) => {
 *   console.log(`Playback started successfully at ${event.time} seconds.`);
 * });
 *
 * ```
 *
 * Custom event example:
 *
 * ```ts
 * import { PlayerCore, CoreEvent } from '@nrk/player-core';
 *
 * const container: HTMLDivElement = ...;
 *
 * container.addEventListener(CoreEvent.WaitingChange,
 *   (event) => toggleSpinner(event.detail.isWaiting));
 *
 * const core = new PlayerCore(container, ...);
 * ```
 *
 * There's a couple of ways to apply the right TypeScript types to the custom
 * event detail property, other than casting the detail value directly:
 *
 * a) By casting the container element:
 *
 * ```ts
 * import { CoreEvent, CoreCustomEventTarget } from '@nrk/player-core';
 *
 * const container: HTMLDivElement = ...;
 *
 * (container as CoreCustomEventTarget)
 *   .addEventListener(CoreEvent.PlayingChange, (event) => {
 *     // Detail type is now PlayingChangeEvent:
 *     if (event.detail.isPlaying) {
 *       console.log('Playing!');
 *     }
 *   });
 * ```
 *
 * b) By using a type guard:
 *
 * ```ts
 * import { CoreEvent, CoreCustomEventDetail } from '@nrk/player-core';
 *
 * function isCoreCustomEvent<T extends keyof CoreCustomEventDetail>(
 *   event: Event,
 *   eventType: T
 * ): event is CustomEvent<CoreCustomEventDetail[T]> {
 *   return event.type === eventType;
 * }
 *
 * function eventHandler(event: Event): void {
 *   if (isCoreCustomEvent(event, CoreEvent.TimeRunningChange)) {
 *     // Detail type is now PlayingChangeEvent:
 *     if (event.detail.isPlaying) {
 *       console.log('Playing!');
 *     }
 *   }
 * }
 *
 * const container: HTMLDivElement = ...;
 * container.addEventListener(CoreEvent.TimeRunningChange, eventHandler);
 * ```
 */
let CoreEvent;

(function (CoreEvent) {
  CoreEvent["SessionCreated"] = "playercore.sessioncreated";
  CoreEvent["SessionClosed"] = "playercore.sessionclosed";
  CoreEvent["PlayingChange"] = "playercore.playingchange";
  CoreEvent["WaitingChange"] = "playercore.waitingchange";
  CoreEvent["TimeRunningChange"] = "playercore.timerunningchange";
  CoreEvent["Broken"] = "playercore.broken";
  CoreEvent["MediaInfoLoaded"] = "playercore.mediainfoloaded";
  CoreEvent["LiveStreamEnded"] = "playercore.livestreamended";
  CoreEvent["RateChange"] = "playercore.ratechange";
  CoreEvent["VolumeChange"] = "playercore.volumechange";
  CoreEvent["MutedChange"] = "playercore.mutedchange";
  CoreEvent["TimeUpdate"] = "playercore.timeupdate";
  CoreEvent["ScrubberUpdate"] = "playercore.scrubberupdate";
  CoreEvent["MediaRangeChange"] = "playercore.mediarangechange";
  CoreEvent["QualityChange"] = "playercore.qualitychange";
  CoreEvent["FullscreenStateChange"] = "playercore.fullscreenstatechange";
  CoreEvent["PiPStateChange"] = "playercore.pipstatechange";
  CoreEvent["RemotePlaybackStateChange"] = "playercore.remoteplaybackstatechange";
  CoreEvent["SubtitlesChange"] = "playercore.subtitleschange";
  CoreEvent["SubtitlesDisplayChange"] = "playercore.subtitlesdisplaychange";
  CoreEvent["SubtitlesCueUpdate"] = "playercore.subtitlescueupdate";
})(CoreEvent || (CoreEvent = {}));

let PlayingChangeType;

(function (PlayingChangeType) {
  PlayingChangeType["Starting"] = "starting";
  PlayingChangeType["Replay"] = "replay";
})(PlayingChangeType || (PlayingChangeType = {}));

export { CoreEvent as C, PlaybackState as P, WaitingReason as W, PausedReason as a, PlayingChangeType as b };
